import type { InitOptions } from 'i18next';

/**
 * Get i18n settings for i18next.
 * @param languages
 * @param language
 * @param namespaces
 */
export function createI18nSettings({
  languages,
  language,
  namespaces,
}: {
  languages: string[];
  language: string;
  namespaces?: string | string[];
}): InitOptions {
  const lng = language;
  const ns = namespaces;

  return {
    supportedLngs: languages,
    fallbackLng: languages[0],
    detection: undefined,
    lng,
    preload: false as const,
    lowerCaseLng: true as const,
    fallbackNS: ns,

    // 🔴 Ohne `saveMissing` feuert i18next das Ereignis `missingKey` NIE — der
    // Auloeser liegt innerhalb von `if (this.options.saveMissing)` (i18next
    // 26.0.2). Der Standard ist `false`.
    //
    // Das hiess: Jeder Zuhoerer auf `missingKey` war toter Code, und die
    // Abwesenheit von Meldungen war kein Beleg fuer irgendetwas. Genau daran
    // scheiterte die Frage aus #836, ob in Produktion je rohe Schluessel
    // ausgeliefert wurden.
    saveMissing: true,

    // Verhindert, dass i18next versucht, fehlende Schluessel ans Backend zu
    // SCHREIBEN. Ist ein `missingKeyHandler` gesetzt, nimmt i18next diesen Weg
    // statt `backendConnector.saveMissing` — das Ereignis wird danach trotzdem
    // ausgeloest. Bewusst leer: gemeldet wird im Aufrufer, der den
    // Ueberwachungsdienst kennt.
    missingKeyHandler: () => {},

    missingInterpolationHandler: (text, value, options) => {
      console.debug(
        `Missing interpolation value for key: ${text}`,
        value,
        options,
      );
    },
    ns,

    // 🔴 REACT ESCAPT SELBST — i18next darf es NICHT noch einmal tun (03.09.2026).
    //
    // Ohne diese Zeile steht i18nexts Standard `escapeValue: true`. Ein interpolierter Wert mit
    // `&` wird dann zu `&amp;`, React escapt das `&` ein zweites Mal, und der Nutzer liest
    // woertlich „&amp;". Beobachtet auf der Ankunftsseite: „Die 3-Zimmer mit Balkon &amp;
    // Terrasse … ist online" — waehrend dieselbe Ueberschrift in der Kopfzeile darueber korrekt
    // stand.
    //
    // Warum es nur SERVERSEITIG auffiel: `i18n.client.ts:48` setzte `escapeValue: false` bereits
    // fuer sich, `i18n.server.ts` nicht. Dieselbe Zeichenkette sah also je nach Rendering-Ort
    // anders aus. Deshalb steht die Einstellung jetzt hier, in den GEMEINSAMEN Einstellungen —
    // eine zweite lokale Kopie waere genau der Grund, warum es beim naechsten Mal wieder
    // auseinanderlaeuft.
    //
    // Sicherheit: das ist keine Lockerung. React escapt jeden interpolierten Wert in JSX; roh
    // eingefuegtes HTML entstuende nur ueber `dangerouslySetInnerHTML`, und das ist von dieser
    // Einstellung unberuehrt.
    interpolation: {
      escapeValue: false,
    },

    react: {
      useSuspense: true,
    },
  };
}
